"use client";

import type { ProductDetails, Variant } from "@raumagarn/formatters";
import { withSearchParams } from "@trydig/ui/next";
import { useState } from "react";
import { MediaGallery } from "~/components/ui/MediaGallery";
import { YarnAddToCart } from "~/components/ui/YarnPDPContainer/YarnAddToCart";
import { YarnDisplayText } from "~/components/ui/YarnSelector/YarnDisplayText";
import { YarnPreviewGrid } from "~/components/ui/YarnSelector/YarnPreviewGrid";
import { YarnShowMore } from "~/components/ui/YarnSelector/YarnShowMore";
import { VariantContext } from "~/features/products/VariantContext";
import { useDatasource } from "~/lib/storyblok/useDatasource";

type TClientPDPContainer = {
	above: React.ReactNode;
	below: React.ReactNode;
	product: ProductDetails;
};

export const YarnPDPContainer = withSearchParams<TClientPDPContainer>(
	({ above, below, product, searchParams }) => {
		const [previewOpen, setPreviewOpen] = useState(false);
		const [showAll, setShowAll] = useState(false);

		const getLabel = useDatasource("pdp-text");
		const cardsPerRow = 7;
		const rows = 2;

		const variantId = searchParams.get("variant") || product.displayItemId;

		const selected = product.variants?.find((v) => v.displayItemId === variantId) as Variant;

		const firstVariants = product.variants.slice(0, cardsPerRow * rows);
		const hiddenYarns = product.variants.length - firstVariants.length;

		return (
			<VariantContext
				value={{
					variants: product.variants as Variant[],
					currentVariant: selected,
				}}
			>
				<div data-layout="section" className="pdp-container">
					{/* desktop gallery */}
					<div className="pdp-gallery">
						<MediaGallery media={(selected || product).media} />
					</div>
					{/* product details */}
					<div data-layout="section" className="pdp-details">
						{above}
						{selected && <YarnDisplayText>{selected.variantName}</YarnDisplayText>}
						<YarnPreviewGrid setPreviewOpen={setPreviewOpen} showAll={showAll} />
						{!showAll && (hiddenYarns ?? 0) >= 1 && (
							<YarnShowMore onClick={() => setShowAll(true)}>
								+ {hiddenYarns} {getLabel("flere farger")}
							</YarnShowMore>
						)}
						<YarnAddToCart
							yarn={selected}
							open={previewOpen}
							onOpenChange={setPreviewOpen}
							product={product}
						/>
						{below}
					</div>
				</div>
			</VariantContext>
		);
	},
);
